/*
Leaving with saves pending (XOL-596, plan R2.18 and UF7, UX spec item 8).

"Save before page leave if there is a save pending. Block leave if save fails and
display error as if any other save fail." Nothing is discarded: saving is attempted
first. Three exits are covered here; a drawer close is guarded by the drawer itself
(LayerHost leave guard, slice 2) and is NOT guarded twice.

A. IN-APP NAVIGATION: `createLeaveGuard(getRoot)` is a router.beforeEach guard.
   - Nothing pending outside the self-guarded drawers: `next()` in the same tick
     (a synchronous "needed?" check first; vue-router 2.8.1 pushes the old URL back
     on `next(false)` during a Back press, so a navigation that needs nothing must
     never be vetoed).
   - Otherwise the navigation is HELD, never vetoed and re-issued: every pending
     save is started at once and awaited (the save-status card reads "Saving your
     changes…" at once, C-12), and
     `next()` is called when they all succeed. A save that fails: `next(false)`, the
     normal notice for that failure plus "This page stays open until your changes
     are saved." (the burst's `leave` reason). A save that HANGS: the hang dialog
     (Keep waiting / Leave now); Leave now lets the navigation go.
   - Saves that fail on the NETWORK (or a 5xx) hold the navigation instead of
     failing it (Carlos, 2026-10-08): the fields are retried on a back-off, a retry
     that succeeds lets the navigation go, and after 30 s of continuous failure a
     dialog asks Keep trying / Close without saving (`saveWait.keepTrying`; the
     second drops the pending fields and lets the navigation go). 409 / 403 / 422
     still fail it at once. Logout is not part of this: it has its own dialog.
   - An open EditPane form (ActionDetails, ProgressStep) is a DRAFT: never flushed.
B. LOGOUT (UF7). User logout hard-reloads, which wipes every notice, so a failed
   save cannot be left to a notice. The `/logout` navigation runs the same flush,
   and if any save failed, the stash still holds values from an undismissed "not
   saved" notice, or an EditPane draft is open, a dialog lists what will be lost
   (C-1 groups with Copy): "Log out anyway" (danger) or "Stay" (Esc = Stay; initial
   focus on the first Copy, else Stay). Logout itself is never blocked: Log out
   anyway goes ahead, and the leave bypass stops any other guard of that navigation
   (a drawer's own) from holding it again.
C. RELOAD / TAB CLOSE / TYPED URL: `installPageLeave(win)` starts every pending field
   save as a `fetch` keepalive request (lib/keepalive.js) on `beforeunload` and
   `pagehide` and returns the browser's own leave prompt while anything is pending, in
   flight, failed or a draft. The prompt text is the browser's; iOS Safari often shows
   none (pagehide still starts the saves).

A session teardown (a forced 401, the client-gate logout) saves nothing and shows
nothing (`isTearingDown`): the tokens are already dead.

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import * as sync from '@/lib/requestRowSync'
import { isTearingDown } from '@/lib/session'
import { waitForSaves, keepTrying } from '@/lib/saveWait'
import * as savePopup from '@/lib/savePopup'
import { copyText } from '@/lib/requestSyncCopy'
import { groupsFor } from '@/lib/syncNotices'
import StaleSaveNotice from '@/components/requests/StaleSaveNotice'

export function isLogoutPath (path) {
  var p = path || ''
  return p === '/logout' || p.indexOf('/logout/') === 0 || p.indexOf('/logout?') === 0
}

/* ------------------------------------------------------------------------
   The flush with its "Saving your changes…" message and the hang dialog
------------------------------------------------------------------------ */

// Flush and wait. Resolves { left, result }: `left` when the user chose Leave now at
// the hang dialog (the saves are still running), else `result` is the flush outcome.
function flushAndWait (root, flushOpts) {
  // C-12: the card reads "Saving your changes…" at once (no 300 ms wait).
  var end = savePopup.leaving()

  return waitForSaves(root, sync.flushAllAndWait(flushOpts)).then(function (r) {
    end()
    return r
  })
}

/* ------------------------------------------------------------------------
   A. The global navigation guard
------------------------------------------------------------------------ */

/**
 * @param {Function} getRoot  returns the app root (`$message` / `$confirm`) or null
 *                            before it exists
 * @return {Function} a router.beforeEach guard (to, from, next)
 */
export function createLeaveGuard (getRoot) {
  // One run at a time: a second navigation while the first waits shares the answer
  // (one dialog, not two).
  var run = null

  return function leaveGuard (to, from, next) {
    // A different navigation than the one the user gave up its saves for.
    sync.clearLeaveBypass(to.fullPath)

    var root = getRoot()

    if (isTearingDown() || !root) {
      next()
      return
    }

    if (isLogoutPath(to.path)) {
      guardLogout(root, to, next)
      return
    }

    // Synchronous "needed?": nothing to save (or only the drawers that guard
    // themselves) leaves the navigation untouched.
    if (!sync.hasUnguardedPendingSaves(to.fullPath)) {
      next()
      return
    }

    if (!run) {
      run = flushAndWait(root, { reason: 'leave', unguarded: true }).then(function (r) {
        if (r.left) {
          // Leave now: the user has given these saves up.
          sync.setLeaveBypass(to.fullPath)
          return true
        }
        if (r.result && r.result.ok) {
          return true
        }
        // Saves that failed on the network: keep trying, and ask after 30 s
        // (Carlos, 2026-10-08). Anything else fails the navigation as before.
        return keepTrying(root, r.result, function () {
          return sync.flushAllAndWait({ reason: sync.REASON_KEEP_TRYING, unguarded: true })
        }, {
          pending: function () { return sync.pendingFields({ unguarded: true }) },
          abandon: function () { sync.discardUnsaved({ unguarded: true }) }
        }).then(function (k) {
          return k.close
        })
      }).then(function (ok) {
        run = null
        return ok
      }, function () {
        run = null
        return false
      })
    }

    run.then(function (ok) {
      if (ok) {
        next()
      } else {
        // The failed save has already raised its notice (with the "stays open"
        // footer); the navigation does not happen.
        next(false)
      }
    })
  }
}

/* ------------------------------------------------------------------------
   B. Logout
------------------------------------------------------------------------ */

// Everything the user would lose by logging out now, as C-1 items: saves that
// failed (still dirty), the stash of undismissed "not saved" notices, and the open
// EditPane drafts.
export function lostItems () {
  var items = []
  var seen = {}
  var add = function (item) {
    var key = item.kind + ':' + item.id + ':' + item.path
    if (!seen[key]) {
      seen[key] = true
      items.push(item)
    }
  }

  sync.getStash().forEach(function (s) {
    add({
      kind: s.rowKind,
      id: s.rowId,
      path: s.path,
      value: s.yours,
      serverValue: s.serverValue,
      meta: s.meta || { kind: s.rowKind, id: s.rowId, rtx: s.actionType, actionId: s.rowKind === 'action' ? s.rowId : null, stepNo: s.stepNo, partyNo: s.partyNo }
    })
  })

  sync.pendingFields({ drafts: true }).forEach(function (p) {
    add({ kind: p.meta.kind, id: p.meta.id, path: p.path, value: p.yours, meta: p.meta })
  })

  return items
}

// The "Your last change wasn't saved" dialog. Resolves true to log out anyway.
export function logoutDialog (root, items) {
  var h = root.$createElement
  var vnode = h(StaleSaveNotice, {
    props: {
      groups: groupsFor(root, items),
      lead: copyText('requestSync.logout.lead', { n: items.length })
    }
  })

  var shown = root.$confirm(vnode, copyText('requestSync.logout.title'), {
    confirmButtonText: copyText('requestSync.logout.confirm'),
    confirmButtonClass: 'el-button--danger',
    cancelButtonText: copyText('requestSync.logout.cancel'),
    type: 'warning',
    closeOnClickModal: false,
    closeOnPressEscape: true,
    customClass: 'xol-reload-box xol-logout-box'
  })

  // Initial focus: the first Copy, else Stay. Element focuses the confirm button
  // ("Log out anyway") by itself; Enter must never throw listed text away unseen.
  setTimeout(function () {
    focusSafely()
  }, 0)

  return shown.then(function () {
    return true
  }, function () {
    // Stay, Esc, the close box.
    return false
  })
}

function focusSafely () {
  try {
    var box = document.querySelector('.xol-logout-box')
    if (!box) {
      return
    }
    var target = box.querySelector('.ssn-label .el-button') || box.querySelector('.el-message-box__btns .el-button:not(.el-button--danger)')
    if (target) {
      target.focus()
    }
  } catch (e) {
    // focus is a courtesy
  }
}

// The /logout navigation. Logout is never blocked: only the dialog's "Stay" (the
// user's own choice) keeps the session.
function guardLogout (root, to, next) {
  var go = function () {
    sync.setLeaveBypass(to.fullPath)
    next()
  }

  var settle = sync.hasPendingSaves() ? flushAndWait(root, { reason: 'logout' }) : Promise.resolve({ left: false, result: { ok: true, failed: [] } })

  settle.then(function (r) {
    // Leave now at the hang dialog: that dialog already listed what is lost.
    if (r.left) {
      go()
      return null
    }

    var items = lostItems()

    if (items.length === 0) {
      go()
      return null
    }

    return logoutDialog(root, items).then(function (logOut) {
      if (logOut) {
        go()
      } else {
        next(false)
      }
    })
  }).catch(function (e) {
    // Whatever went wrong here, logout is not blocked.
    console.error('leaveGuard: the logout check threw', e)
    go()
  })
}

/* ------------------------------------------------------------------------
   C. Reload / tab close / typed URL
------------------------------------------------------------------------ */

/**
 * `beforeunload`: start the pending saves with keepalive, and return the browser's
 * leave prompt while anything is pending, in flight, failed or a draft. During a
 * teardown, or with nothing to lose, it does nothing (no prompt).
 */
export function onBeforeUnload (event) {
  if (isTearingDown() || !sync.hasUnsavedWork()) {
    return undefined
  }

  sync.startKeepaliveSaves()

  if (event) {
    event.preventDefault()
    event.returnValue = ''
  }
  return ''
}

/** `pagehide` (iOS Safari often shows no prompt): start the saves, no prompt. */
export function onPageHide () {
  if (isTearingDown()) {
    return
  }
  sync.startKeepaliveSaves()
}

/**
 * Attach both listeners once. Returns a function that removes them (tests).
 */
export function installPageLeave (win) {
  var w = win || window

  w.addEventListener('beforeunload', onBeforeUnload)
  w.addEventListener('pagehide', onPageHide)

  return function () {
    w.removeEventListener('beforeunload', onBeforeUnload)
    w.removeEventListener('pagehide', onPageHide)
  }
}
